import React, { useState, useEffect } from 'react';
import { TextField, Box, Typography, Paper, Grid } from '@mui/material';

// The component now accepts props to set initial values
function PriceAndProfitCalculator({
  salesCost = Number(0),
  commissionFixed = Number(0),
  commissionPercentage = Number(0),
  discountFixed = Number(0),
  discountPercentage = Number(0),
  couponAmount = Number(0),
  couponPercentage = Number(0),
  shippingCost = Number(0),
  shippingPercentage = Number(0),
  applicationCharges = Number(0),
  applicationPercentage = Number(0),
  additionalShippingCost = Number(0)
}) {
  // Use props as the initial state values
  const [form, setForm] = useState({
    salesCost: salesCost,
    commissionFixed: commissionFixed,
    commissionPercentage: commissionPercentage,
    discountFixed: discountFixed,
    discountPercentage: discountPercentage,
    couponAmount: couponAmount,
    couponPercentage: couponPercentage,
    shippingCost: shippingCost,
    shippingPercentage: shippingPercentage,
    applicationCharges: applicationCharges,
    applicationPercentage: applicationPercentage,
    additionalShippingCost: additionalShippingCost,
    
    // Calculated fields
    salePrice: Number(0),
    salePriceAfterDiscount: Number(0),
    profitAmount: Number(0),
    profitPercentage: Number(0),
    actualProfit: Number(0),
    
    // Fields to store the calculated dollar values for each cost
    calculatedDiscount: Number(0),
    calculatedCoupon: Number(0),
    calculatedShipping: Number(0),
    calculatedApplication: Number(0),
  });

  // NEW: Effect to update state when props change
  useEffect(() => {
    setForm((prevForm) => ({
      ...prevForm,
      salesCost: salesCost,
      commissionFixed: commissionFixed,
      commissionPercentage: commissionPercentage,
      discountFixed: discountFixed,
      discountPercentage: discountPercentage,
      couponAmount: couponAmount,
      couponPercentage: couponPercentage,
      shippingCost: shippingCost,
      shippingPercentage: shippingPercentage,
      applicationCharges: applicationCharges,
      applicationPercentage: applicationPercentage,
      additionalShippingCost: additionalShippingCost,
    }));
  }, [salesCost, commissionFixed, commissionPercentage, discountFixed, discountPercentage, couponAmount, couponPercentage, shippingCost, shippingPercentage, applicationCharges, applicationPercentage, additionalShippingCost]);


  // Handle changes for all input fields generically
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: parseFloat(value) || 0, // Ensure value is a number
    }));
  };

  // useEffect hook to re-calculate the totals whenever any form value or prop changes
  useEffect(() => {
    const calculateTotals = () => {
      // Use the internal form state for calculations
      const {
        salesCost,
        commissionFixed,
        commissionPercentage,
        discountFixed,
        discountPercentage,
        couponAmount,
        couponPercentage,
        shippingCost,
        shippingPercentage,
        applicationCharges,
        applicationPercentage,
        additionalShippingCost
      } = form;

  
      // Stage 1: Calculate the initial sales price and gross profit
      let finalPrice = Number(salesCost);

      // Add fixed commission and commission based on a percentage of salesCost
      let totalCommission = Number(commissionFixed) + (Number(salesCost) * Number(commissionPercentage) / 100);
      finalPrice += totalCommission;

      // Calculate the preliminary profit (gross profit)
      const profit = finalPrice - Number(salesCost);

      // Stage 2: Calculate all deductions and the final actual profit
      
      // Calculate Discount
      let totalDiscount = 0;
      if (Number(discountFixed) > 0) {
        totalDiscount = Number(discountFixed);
      } else if (Number(discountPercentage) > 0) {
        totalDiscount = (finalPrice * Number(discountPercentage) / 100);
      }

      // Calculate Coupon Cost
      let totalCoupon = 0;
      if (Number(couponAmount) > 0) {
        totalCoupon = Number(couponAmount);
      } else if (Number(couponPercentage) > 0) {
        totalCoupon = (finalPrice * Number(couponPercentage) / 100);
      }
      
      let salesPriceAfterDiscount = finalPrice - Number(totalDiscount);

      // Calculate Shipping Cost
      let totalShipping = 0;
      if (Number(shippingCost) > 0) {
        totalShipping = Number(shippingCost);
      } else if (Number(shippingPercentage) > 0) {
        totalShipping = profit * Number(shippingPercentage) / 100;
      }

      // Calculate Application Charges
      let totalApplication = 0;
      if (Number(applicationCharges) > 0) {
        totalApplication = Number(applicationCharges);
      } else if (Number(applicationPercentage) > 0) {
        totalApplication = profit * Number(applicationPercentage) / 100;
      }
      
      // Calculate actual profit by deducting all costs from the initial profit
      const actualProfit = profit - totalDiscount - totalCoupon - totalShipping - totalApplication - Number(additionalShippingCost);

      // Calculate profit percentage
      const profitPercent = Number(salesCost) > 0 ? (actualProfit / Number(salesCost)) * 100 : 0;


      // Update the state with the new calculated values
      setForm((prevForm) => ({
        ...prevForm,
        salePrice: Number(finalPrice),
        salePriceAfterDiscount: Number(salesPriceAfterDiscount),
        profitAmount: profit,
        profitPercentage: profitPercent,
        actualProfit: actualProfit,
        calculatedDiscount: totalDiscount,
        calculatedCoupon: totalCoupon,
        calculatedShipping: totalShipping + Number(additionalShippingCost),
        calculatedApplication: totalApplication,
      }));
    };

    calculateTotals();
  }, [form.salesCost, form.commissionFixed, form.commissionPercentage, form.discountFixed, form.discountPercentage, form.couponAmount, form.couponPercentage, form.shippingCost, form.shippingPercentage, form.applicationCharges, form.applicationPercentage, form.additionalShippingCost]);

  return (
    <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Product Price and Profit Calculator
        </Typography>
        <Grid container spacing={4}>
          {/* Column 1: Input Fields */}
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom>Input Values</Typography>
            <TextField
              fullWidth
              label="Sales Cost"
              name="salesCost"
              type="number"
              value={form.salesCost}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Fixed Commission"
              name="commissionFixed"
              type="number"
              value={form.commissionFixed}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Commission Percentage (%)"
              name="commissionPercentage"
              type="number"
              value={form.commissionPercentage}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Discount Fixed Amount"
              name="discountFixed"
              type="number"
              value={form.discountFixed}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Discount Percentage (%)"
              name="discountPercentage"
              type="number"
              value={form.discountPercentage}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Coupon Amount"
              name="couponAmount"
              type="number"
              value={form.couponAmount}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Coupon Percentage (%)"
              name="couponPercentage"
              type="number"
              value={form.couponPercentage}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Shipping Cost (Fixed)"
              name="shippingCost"
              type="number"
              value={form.shippingCost}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Shipping Percentage (%)"
              name="shippingPercentage"
              type="number"
              value={form.shippingPercentage}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Application Charges (Fixed)"
              name="applicationCharges"
              type="number"
              value={form.applicationCharges}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Application Percentage (%)"
              name="applicationPercentage"
              type="number"
              value={form.applicationPercentage}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Additional Shipping Cost"
              name="additionalShippingCost"
              type="number"
              value={form.additionalShippingCost}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>
          
          {/* Column 2: Calculated Cost Breakdown */}
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom>Cost Breakdown</Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                **Discount Cost:** **${form.calculatedDiscount.toFixed(2)}**
              </Typography>
              <Typography variant="body1">
                **Coupon Cost:** **${form.calculatedCoupon.toFixed(2)}**
              </Typography>
              <Typography variant="body1">
                **Shipping Cost:** **${form.calculatedShipping.toFixed(2)}**
              </Typography>
              <Typography variant="body1">
                **Application Charges:** **${form.calculatedApplication.toFixed(2)}**
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Final Totals Section at the bottom */}
        <Box sx={{ mt: 4, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
          <Typography variant="h6" align="center">
            Final Calculations
          </Typography>
          <Grid container justifyContent="center" spacing={2} sx={{ mt: 1 }}>
            <Grid item>
              <Typography variant="body1">
                Sales Price: **${form.salePrice.toFixed(2)}**
              </Typography>
            </Grid>

         <Grid item>
              <Typography variant="body1">
                Sales Price after discount: **${form.salePriceAfterDiscount.toFixed(2)}**
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                Initial Profit: **${form.profitAmount.toFixed(2)}**
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" color="primary">
                Actual Profit: **${form.actualProfit.toFixed(2)}**
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" color="primary">
                Profit Percentage: **{form.profitPercentage.toFixed(2)}%**
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default PriceAndProfitCalculator;
